
import { supabase } from './supabase';
import { createPlaceUrl } from './place-url-utils';

export interface SitemapURL {
  url: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: number;
  lastmod?: string;
  images?: Array<{
    url: string;
    title?: string;
    caption?: string;
  }>;
}

export interface SitemapStats {
  totalUrls: number;
  lastGenerated: string;
  categories: {
    static: number;
    destinations: number;
    collections: number;
    places: number;
    search: number;
    countries: number;
    blogs: number;
  };
}

// Static routes that are always present
const staticRoutes: SitemapURL[] = [
  { url: '/', changefreq: 'daily', priority: 1.0 },
  { url: '/search', changefreq: 'daily', priority: 0.9 },
  { url: '/destinations', changefreq: 'daily', priority: 0.9 },
  { url: '/collections', changefreq: 'daily', priority: 0.8 },
  { url: '/blogs', changefreq: 'daily', priority: 0.8 },
  { url: '/hosts', changefreq: 'daily', priority: 0.8 },
  { url: '/recommendations', changefreq: 'weekly', priority: 0.7 },
  { url: '/visa-requirements', changefreq: 'monthly', priority: 0.7 },
  { url: '/explore', changefreq: 'weekly', priority: 0.7 },
  { url: '/trips', changefreq: 'weekly', priority: 0.6 },
  { url: '/wizard', changefreq: 'weekly', priority: 0.6 },
  { url: '/about', changefreq: 'monthly', priority: 0.5 },
  { url: '/contact', changefreq: 'monthly', priority: 0.5 },
  { url: '/help-center', changefreq: 'weekly', priority: 0.6 },
  { url: '/safety', changefreq: 'monthly', priority: 0.6 },
  { url: '/auth', changefreq: 'monthly', priority: 0.4 },
  // Legal pages
  { url: '/terms', changefreq: 'yearly', priority: 0.3 },
  { url: '/privacy', changefreq: 'yearly', priority: 0.3 },
  { url: '/sitemap', changefreq: 'monthly', priority: 0.4 }
];

// Enhanced helper function to create SEO-friendly slugs
function createSlug(text: string): string {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .trim()
    // Replace accented characters with their base equivalents
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Replace special characters and symbols with spaces
    .replace(/[^\w\s-]/g, ' ')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Replace spaces with hyphens
    .replace(/\s/g, '-')
    // Replace multiple hyphens with single hyphen
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Limit length to keep URLs manageable
    .substring(0, 60);
}

// Enhanced function to create readable place type slugs
function createPlaceTypeSlug(placeType: string): string {
  if (!placeType) return 'place';
  
  const typeMapping: { [key: string]: string } = {
    'restaurant': 'restaurants',
    'hotel': 'hotels',
    'attraction': 'attractions',
    'museum': 'museums',
    'park': 'parks',
    'beach': 'beaches',
    'shopping': 'shopping',
    'nightlife': 'nightlife',
    'cafe': 'cafes',
    'bar': 'bars',
    'activity': 'activities',
    'tour': 'tours',
    'festival': 'festivals',
    'event': 'events'
  };
  
  const normalizedType = placeType.toLowerCase().trim();
  return typeMapping[normalizedType] || createSlug(placeType);
}

// Generate destination URLs
async function getDestinationUrls(): Promise<SitemapURL[]> {
  try {
    const { data: destinations, error } = await supabase
      .from('destinations')
      .select('id, name, country, updated_at, image_url, description')
      .order('country')
      .order('name');

    if (error) {
      console.error('Error fetching destinations:', error);
      return [];
    }

    if (!destinations?.length) return [];

    return destinations.map(dest => {
      const countrySlug = createSlug(dest.country);
      const nameSlug = createSlug(dest.name);
      
      const url: SitemapURL = {
        url: `/destinations/${countrySlug}/${nameSlug}/${dest.id}`,
        changefreq: 'weekly',
        priority: 0.8,
        lastmod: dest.updated_at
      };

      // Add image if available
      if (dest.image_url) {
        url.images = [{
          url: dest.image_url,
          title: `${dest.name} - Beautiful destination in ${dest.country}`,
          caption: dest.description?.substring(0, 200) || 
                   `Discover ${dest.name}, a stunning travel destination in ${dest.country}. Find the best places to visit, local experiences, and travel tips.`
        }];
      }

      return url;
    });
  } catch (error) {
    console.error('Error in getDestinationUrls:', error);
    return [];
  }
}

// Generate collection URLs
async function getCollectionUrls(): Promise<SitemapURL[]> {
  try {
    const { data: collections, error } = await supabase
      .from('destination_playlists')
      .select('id, name, city, country, updated_at, description')
      .eq('is_public', true)
      .order('country')
      .order('city')
      .order('name');

    if (error) {
      console.error('Error fetching collections:', error);
      return [];
    }

    if (!collections?.length) {
      console.log('No public collections found');
      return [];
    }

    console.log(`Found ${collections.length} public collections`);

    return collections.map(collection => {
      const nameSlug = createSlug(collection.name);
      const locationSlug = collection.city ? 
        `${createSlug(collection.city)}-${createSlug(collection.country)}` :
        createSlug(collection.country);
      
      return {
        url: `/collections/${locationSlug}/${nameSlug}/${collection.id}`,
        changefreq: 'weekly',
        priority: 0.7,
        lastmod: collection.updated_at
      };
    });
  } catch (error) {
    console.error('Error in getCollectionUrls:', error);
    return [];
  }
}

// Generate individual place URLs with enhanced SEO-friendly URLs
async function getPlaceUrls(): Promise<SitemapURL[]> {
  try {
    // First, let's get all places from public collections
    const { data: places, error } = await supabase
      .from('destination_playlist_items')
      .select(`
        id, 
        name, 
        place_type, 
        city, 
        country, 
        updated_at, 
        photos,
        note,
        rating,
        address,
        playlist_id,
        destination_playlists!inner(
          id,
          name,
          is_public
        )
      `)
      .not('name', 'is', null)
      .not('city', 'is', null)
      .not('country', 'is', null)
      .eq('destination_playlists.is_public', true);

    if (error) {
      console.error('Error fetching places:', error);
      return [];
    }

    if (!places?.length) {
      console.log('No places found from public collections');
      return [];
    }

    console.log(`Found ${places.length} places from public collections`);

    return places.map(place => {
      // Use the centralized createPlaceUrl function for consistency
      const url: SitemapURL = {
        url: createPlaceUrl({
          id: place.id,
          name: place.name,
          city: place.city,
          country: place.country,
          place_type: place.place_type
        }),
        changefreq: 'weekly',
        priority: 0.6,
        lastmod: place.updated_at
      };

      // Add rich image information for better SEO
      if (place.photos?.length > 0) {
        url.images = place.photos.slice(0, 3).map((photo: string, index: number) => ({
          url: photo,
          title: `${place.name} in ${place.city}, ${place.country} - Photo ${index + 1}`,
          caption: place.note?.substring(0, 200) || 
                   `Discover ${place.name}, a ${place.place_type?.toLowerCase() || 'place'} in ${place.city}, ${place.country}. ${place.rating ? `Rated ${place.rating}/5 stars.` : ''}`
        }));
      }

      return url;
    });
  } catch (error) {
    console.error('Error in getPlaceUrls:', error);
    return [];
  }
}

// Generate popular search term URLs
function getSearchUrls(): SitemapURL[] {
  const popularSearchTerms = [
    'paris', 'tokyo', 'rome', 'london', 'new-york', 'bali', 'barcelona', 'amsterdam',
    'thailand', 'greece', 'spain', 'italy', 'japan', 'france', 'portugal', 'morocco',
    'restaurants', 'museums', 'beaches', 'nightlife', 'shopping', 'cafes', 'hotels'
  ];
  
  return popularSearchTerms.map(term => ({
    url: `/search?q=${encodeURIComponent(term)}`,
    changefreq: 'daily',
    priority: 0.5,
    lastmod: new Date().toISOString()
  }));
}

// Generate blog post URLs
async function getBlogUrls(): Promise<SitemapURL[]> {
  try {
    const { data: blogPosts, error } = await supabase
      .from('community_posts')
      .select('id, title, city, country, updated_at, content')
      .eq('type', 'blog')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }

    if (!blogPosts?.length) return [];

    return blogPosts.map(post => {
      const titleSlug = createSlug(post.title);
      const locationSlug = post.city && post.country ? 
        `${createSlug(post.city)}-${createSlug(post.country)}` :
        post.country ? createSlug(post.country) : 'travel';
      
      return {
        url: `/blog/${locationSlug}/${titleSlug}/${post.id}`,
        changefreq: 'monthly',
        priority: 0.6,
        lastmod: post.updated_at
      };
    });
  } catch (error) {
    console.error('Error in getBlogUrls:', error);
    return [];
  }
}

// Generate country landing page URLs
async function getCountryUrls(): Promise<SitemapURL[]> {
  try {
    const { data: countries, error } = await supabase
      .from('destinations')
      .select('country')
      .order('country');

    if (error) {
      console.error('Error fetching countries:', error);
      return [];
    }

    if (!countries?.length) return [];

    // Get unique countries
    const uniqueCountries = [...new Set(countries.map(d => d.country))];

    return uniqueCountries.map(country => ({
      url: `/destinations/${createSlug(country)}`,
      changefreq: 'weekly',
      priority: 0.7,
      lastmod: new Date().toISOString()
    }));
  } catch (error) {
    console.error('Error in getCountryUrls:', error);
    return [];
  }
}

// Main function to generate complete sitemap
export async function generateDynamicSitemap(baseUrl: string): Promise<SitemapStats & { urls: SitemapURL[] }> {
  console.log('Starting dynamic sitemap generation...');
  
  try {
    // First, let's check what data we have
    const { data: totalCollections } = await supabase
      .from('destination_playlists')
      .select('id, is_public')
      .eq('is_public', true);
    
    const { data: totalPlaces } = await supabase
      .from('destination_playlist_items')
      .select('id, playlist_id')
      .not('name', 'is', null);

    console.log(`Total public collections in database: ${totalCollections?.length || 0}`);
    console.log(`Total places in database: ${totalPlaces?.length || 0}`);

    // Fetch all dynamic URLs in parallel
    const [
      destinationUrls,
      collectionUrls,
      placeUrls,
      blogUrls,
      countryUrls
    ] = await Promise.all([
      getDestinationUrls(),
      getCollectionUrls(),
      getPlaceUrls(),
      getBlogUrls(),
      getCountryUrls()
    ]);

    const searchUrls = getSearchUrls();

    // Combine all URLs
    const allUrls = [
      ...staticRoutes,
      ...destinationUrls,
      ...collectionUrls,
      ...placeUrls,
      ...searchUrls,
      ...countryUrls,
      ...blogUrls
    ];

    const totalUrls = allUrls.length;
    const lastGenerated = new Date().toISOString();

    console.log(`Generated sitemap with ${totalUrls} URLs:`);
    console.log(`- Static pages: ${staticRoutes.length}`);
    console.log(`- Destinations: ${destinationUrls.length}`);
    console.log(`- Collections: ${collectionUrls.length}`);
    console.log(`- Places: ${placeUrls.length}`);
    console.log(`- Search pages: ${searchUrls.length}`);
    console.log(`- Country pages: ${countryUrls.length}`);
    console.log(`- Blog posts: ${blogUrls.length}`);

    return {
      urls: allUrls,
      totalUrls,
      lastGenerated,
      categories: {
        static: staticRoutes.length,
        destinations: destinationUrls.length,
        collections: collectionUrls.length,
        places: placeUrls.length,
        search: searchUrls.length,
        countries: countryUrls.length,
        blogs: blogUrls.length
      }
    };
  } catch (error) {
    console.error('Error generating dynamic sitemap:', error);
    
    // Return minimal sitemap with static routes only
    return {
      urls: staticRoutes,
      totalUrls: staticRoutes.length,
      lastGenerated: new Date().toISOString(),
      categories: {
        static: staticRoutes.length,
        destinations: 0,
        collections: 0,
        places: 0,
        search: 0,
        countries: 0,
        blogs: 0
      }
    };
  }
}

// Enhanced XML generation with image support
export function generateEnhancedSitemapXML(urls: SitemapURL[], baseUrl: string): string {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">\n';
  
  urls.forEach(entry => {
    xml += '  <url>\n';
    xml += `    <loc>${baseUrl}${entry.url}</loc>\n`;
    if (entry.lastmod) {
      xml += `    <lastmod>${entry.lastmod}</lastmod>\n`;
    }
    xml += `    <changefreq>${entry.changefreq}</changefreq>\n`;
    xml += `    <priority>${entry.priority.toFixed(1)}</priority>\n`;
    
    // Add images if present
    if (entry.images && entry.images.length > 0) {
      entry.images.forEach(image => {
        xml += '    <image:image>\n';
        xml += `      <image:loc>${image.url.replace(/&/g, '&amp;')}</image:loc>\n`;
        
        if (image.title) {
          const escapedTitle = image.title
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
          xml += `      <image:title><![CDATA[${escapedTitle}]]></image:title>\n`;
        }
        
        if (image.caption) {
          const escapedCaption = image.caption
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
          xml += `      <image:caption><![CDATA[${escapedCaption}]]></image:caption>\n`;
        }
        
        xml += '    </image:image>\n';
      });
    }
    
    xml += '  </url>\n';
  });
  
  xml += '</urlset>';
  return xml;
}
